/**
 * Browser adapter (spec 68 §5.3).
 *
 * Re-exports the existing @dash0/sdk-web RUM wiring so app code can
 * `import { bootBrowser } from '@repo/telemetry/browser'` without knowing
 * the client/server boundary. RUM (page views, web vitals, fetch timing,
 * errors) is captured by @dash0/sdk-web itself; the facade's log/track on
 * the browser stay noop in pass-1 (browser product events + per-route
 * traceparent injection land in Phase C/E).
 *
 * Excluded from unit coverage — it only runs in a real browser document.
 */
import { init as dash0WebInit } from '@dash0/sdk-web';
import { resetCaps } from './core/dispatch.js';
import { resetSink } from './core/registry.js';

export interface BootBrowserConfig {
  serviceName: string;
  endpoint?: string;
  authToken?: string;
}

function browserEnv(): Record<string, string | undefined> {
  const p = (globalThis as { process?: { env?: Record<string, string | undefined> } }).process;
  return p?.env ?? {};
}

/**
 * Initialise Dash0 browser RUM. No-ops silently when the endpoint/token
 * envs are absent (dev / CI), mirroring the current
 * apps/web/instrumentation-client.ts guard.
 */
export function bootBrowser(config: BootBrowserConfig): void {
  const env = browserEnv();
  const endpoint = config.endpoint ?? env.NEXT_PUBLIC_DASH0_ENDPOINT;
  const authToken = config.authToken ?? env.NEXT_PUBLIC_DASH0_AUTH_TOKEN;
  if (endpoint && authToken) {
    dash0WebInit({ serviceName: config.serviceName, endpoint: { url: endpoint, authToken } });
  }
  // Pass-1: the facade stays noop on the browser (RUM is independent).
  resetSink();
  resetCaps();
}

export { init as initDash0Web } from '@dash0/sdk-web';

// CON-28 — shared prefetch-abort / RSC-fetch noise filters for every RUM init.
export * from './rum-noise.js';

// Release identity (serviceVersion / deploymentId / environment) so a RUM
// stack trace can be pinned to the build whose source maps resolve it.
export * from './rum-release.js';
